import type { ReactNode, Ref } from 'react'

import type { FunnelStageKey } from '@/api/types'

import { formatCount, formatRate, NEGATIVE_DROP_OFF_NOTE, NO_FIGURE } from './figures'

/** One stage's row, as a funnel decided it: its figures and which of them it may show. */
export interface StageRow {
  key: FunnelStageKey
  /** The stage has figures this period. */
  measured: boolean
  /** The step from the stage above is a figure: both stages were measured. */
  comparable: boolean
  users: number | null
  sessions: number | null
  stageConversionRate: number | null
  totalConversionRate: number | null
  dropOff: number | null
  dropOffRate: number | null
  /** The bar's share of the funnel's whole; null: no bar. */
  share: number | null
  /** What an unmeasured stage's users and sessions read ("Not measured", a reason). */
  missing: ReactNode
}

/**
 * A funnel's stages as a table: users, sessions, conversion from the stage before and
 * from the first, and the drop-off on the way in, with the funnel's own row header and
 * any column of its own after them. A step or a loss into or out of an unmeasured stage
 * reads "—", never 0 %; a negative drop-off gets its note. The Funnel page and a
 * competition's Funnel tab both draw theirs with it.
 */
export function StagesTable<T>({
  caption,
  stages,
  row,
  header,
  extra,
  rowRef,
  selected,
}: {
  caption: string
  stages: readonly T[]
  /** The stage's row, given the stage above it. */
  row: (stage: T, above: T | undefined) => StageRow
  /** The stage's row header: its name, a button, a link. */
  header: (stage: T) => ReactNode
  /** A last column of the funnel's own, folded away on a narrow table like the others. */
  extra?: { label: string; cell: (stage: T) => ReactNode }
  rowRef?: (stage: T) => Ref<HTMLTableRowElement> | undefined
  selected?: (stage: T) => boolean
}) {
  const rows = stages.map((stage, index) => ({ stage, view: row(stage, stages[index - 1]) }))
  const gained = rows.some(({ view }) => view.comparable && (view.dropOff ?? 0) < 0)
  return (
    <>
      <div className="ci-table-wrap fun-table" role="region" aria-label={caption} tabIndex={0}>
        <table className="ci-table">
          <caption className="ci-visually-hidden">{caption}</caption>
          <thead>
            <tr>
              <th scope="col">Stage</th>
              {/* The bar repeats Total conversion: decoration, hidden from assistive technology header and cells alike. */}
              <td className="fun-table__bar-col" aria-hidden="true" />
              <th scope="col" data-align="end">
                Users
              </th>
              <th scope="col" data-align="end">
                Sessions
              </th>
              <th scope="col" data-align="end">
                Step conversion
              </th>
              <th scope="col" data-align="end" className="fun-table__optional">
                Total conversion
              </th>
              <th scope="col" data-align="end" className="fun-table__optional">
                Drop-off
              </th>
              <th scope="col" data-align="end" className="fun-table__optional">
                Drop-off rate
              </th>
              {extra ? (
                <th scope="col" data-align="end" className="fun-table__optional">
                  {extra.label}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ stage, view }) => (
              <tr key={view.key} ref={rowRef?.(stage)} data-stage={view.key} data-selected={selected?.(stage) || undefined}>
                <th scope="row">{header(stage)}</th>
                <td className="fun-table__bar-col" aria-hidden="true">
                  <span className="ci-funnel__track">
                    {view.share !== null ? (
                      <span className="ci-funnel__bar" style={{ width: `${Math.max(1.5, Math.min(1, view.share) * 100)}%` }} />
                    ) : null}
                  </span>
                </td>
                <td data-align="end">{view.measured ? formatCount(view.users) : view.missing}</td>
                <td data-align="end">{view.measured ? formatCount(view.sessions) : view.missing}</td>
                <td data-align="end">{view.comparable ? formatRate(view.stageConversionRate) : NO_FIGURE}</td>
                <td data-align="end" className="fun-table__optional">
                  {view.measured ? formatRate(view.totalConversionRate) : NO_FIGURE}
                </td>
                <td data-align="end" className="fun-table__optional">
                  {view.comparable ? formatCount(view.dropOff) : NO_FIGURE}
                </td>
                <td data-align="end" className="fun-table__optional">
                  {view.comparable ? formatRate(view.dropOffRate) : NO_FIGURE}
                </td>
                {extra ? (
                  <td data-align="end" className="fun-table__optional">
                    {extra.cell(stage)}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {gained ? (
        <p className="fun-note" role="note">
          {NEGATIVE_DROP_OFF_NOTE}
        </p>
      ) : null}
    </>
  )
}
